import validate from 'validate.js'
import emojiRegex from 'emoji-regex'
import { AutomatedTriggerType, CampaignStatus, CampaignType, EmailContentBlockType } from '../../graphQL/generated/enums'
import EmailCampaignModel from '../stores/EmailCampaignStore/EmailCampaignModel'
import SmsCampaignModel from '../stores/SmsCampaignStore/SmsCampaignModel'
import { formatErrorMessage } from './utils'
import moment from 'moment-timezone'

export type CampaignTemplateTypeValues = `${BaseCampaignTemplateType}` | `${AutomatedTriggerType}`;

export const defaultAutomationSettings: Record<AutomatedTriggerType, {
  triggerType: AutomatedTriggerType,
  daysOffset?: number
}> = {
  firstBooking: {
    triggerType: AutomatedTriggerType.FirstBooking,
    daysOffset: 5,
  },
  lapsed: {
    triggerType: AutomatedTriggerType.Lapsed,
    daysOffset: 30,
  },
  offlineBooking: {
    triggerType: AutomatedTriggerType.OfflineBooking,
    daysOffset: 5,
  },
  upcomingBirthday: {
    triggerType: AutomatedTriggerType.UpcomingBirthday,
    daysOffset: -7,
  },
  loyaltyCompleted: {
    triggerType: AutomatedTriggerType.LoyaltyCompleted,
  },
  loyaltyHalf: {
    triggerType: AutomatedTriggerType.LoyaltyHalf,
  },
  loyaltyAlmost: {
    triggerType: AutomatedTriggerType.LoyaltyAlmost,
  },
  clientReferralCompleted: {
    triggerType: AutomatedTriggerType.ClientReferralCompleted,
  },
  clientReferralHalf: {
    triggerType: AutomatedTriggerType.ClientReferralHalf,
  },
  clientReferralAlmost: {
    triggerType: AutomatedTriggerType.ClientReferralAlmost,
  },
  [AutomatedTriggerType.TrBookingAccepted]: {
    triggerType: AutomatedTriggerType.TrBookingAccepted,
  },
  [AutomatedTriggerType.TrRescheduleRejected]: {
    triggerType: AutomatedTriggerType.TrRescheduleRejected,
  },
  [AutomatedTriggerType.TrBookingCancellationRequested]: {
    triggerType: AutomatedTriggerType.TrBookingCancellationRequested,
  },
  [AutomatedTriggerType.TrBookingCancelled]: {
    triggerType: AutomatedTriggerType.TrBookingCancelled,
  },
  [AutomatedTriggerType.TrBookingNoShow]: {
    triggerType: AutomatedTriggerType.TrBookingNoShow,
  },
  [AutomatedTriggerType.TrBookingCompleted]: {
    triggerType: AutomatedTriggerType.TrBookingCompleted,
  },
  [AutomatedTriggerType.TrBookingConsultationFormReminder]: {
    triggerType: AutomatedTriggerType.TrBookingConsultationFormReminder,
  },
  [AutomatedTriggerType.TrBookingDepositReminder]: {
    triggerType: AutomatedTriggerType.TrBookingDepositReminder,
  },
  [AutomatedTriggerType.TrBookingDeclined]: {
    triggerType: AutomatedTriggerType.TrBookingDeclined,
  },
  [AutomatedTriggerType.TrBookingReminder]: {
    triggerType: AutomatedTriggerType.TrBookingReminder,
    daysOffset: -1,
  },
  [AutomatedTriggerType.TrReviewReminder]: {
    triggerType: AutomatedTriggerType.TrReviewReminder,
    daysOffset: 2,
  },
  [AutomatedTriggerType.TrBookingRequested]: {
    triggerType: AutomatedTriggerType.TrBookingRequested,
  },
  [AutomatedTriggerType.TrBookingUpdated]: {
    triggerType: AutomatedTriggerType.TrBookingUpdated,
  },
  [AutomatedTriggerType.TrPaymentReceipt]: {
    triggerType: AutomatedTriggerType.TrPaymentReceipt,
  },
  [AutomatedTriggerType.GcPaymentReceipt]: {
    triggerType: AutomatedTriggerType.GcPaymentReceipt,
  },
  [AutomatedTriggerType.GcRedemptionCode]: {
    triggerType: AutomatedTriggerType.GcRedemptionCode,
  },
}

export type SMSTemplateType = Exclude<CampaignTemplateTypeValues, 'giftCard' | 'loyaltyScheme' | 'clientReferralScheme'>
export const smsTemplates: Record<SMSTemplateType, Pick<SmsCampaign, 'label' | 'messageContent' | 'type'>> = {
  blank: {
    label: 'SMS Broadcast campaign',
    messageContent: '',
    type: CampaignType.Broadcast,
  },
  runPromotion: {
    label: 'Limited time offer',
    messageContent: 'Hi {{ClientFirstName}}, I just wanted to reach out and offer [DISCOUNT_TEXT] for your next booking. Use [PROMOCODE] at checkout on my website {{BusinessWebsite}}.',
    type: CampaignType.Broadcast,
  },
  newTreatments: {
    label: 'New Treatments',
    messageContent: 'Hi {{ClientFirstName}}, I am excited to share that I have added more treatments to my menu, which you can now book on my website {{BusinessWebsite}}.',
    type: CampaignType.Broadcast,
  },
  websiteLaunch: {
    label: 'Website Launch',
    messageContent: 'Hi {{ClientFirstName}}, I just wanted to share my brand new website where you can see my full treatment menu, my availability and book your appointments online. {{BusinessWebsite}}.',
    type: CampaignType.Broadcast,
  },
  upcomingAvailability: {
    label: 'Upcoming appointments',
    messageContent: 'Hi {{ClientFirstName}}, I wanted to let you know that have a few final slots available this week. You can book an appointment via my website {{BusinessWebsite}}.',
    type: CampaignType.Broadcast,
  },
  upcomingBirthday: {
    label: 'Client birthday Automation',
    messageContent: 'Hi {{ClientFirstName}}, your birthday is just around the corner, and I wanted to give you a little something to say thank you for being a loyal customer. Use the code [PROMOCODE] the next time you book at {{BusinessWebsite}}. Look forward to seeing you soon!',
    type: CampaignType.Automated,
  },
  firstBooking: {
    label: 'First Booking Automation',
    messageContent: 'Hi {{ClientFirstName}}, thanks for making your first booking with me. As a thank you I wanted to give you a special discount code for your next booking. Use the code [PROMOCODE] the next time you book online at {{BusinessWebsite}}. Look forward to seeing you soon!',
    type: CampaignType.Automated,
  },
  offlineBooking: {
    label: 'Online Booking Automation',
    messageContent: 'Hi {{ClientFirstName}}, thank you for your recent booking. I hope you had a great experience! I\'m excited to share that I have a new online booking system. Use the code [PROMOCODE] the next time you book online at {{BusinessWebsite}}. Look forward to seeing you soon!',
    type: CampaignType.Automated,
  },
  lapsed: {
    label: 'Lapsed Client Automation',
    messageContent: 'Hi {{ClientFirstName}}, I\'ve noticed that you haven\'t booked with me in a while! To tempt you back I wanted to offer a special discount. Use the code [PROMOCODE] the next time you book online at {{BusinessWebsite}}. Look forward to seeing you soon!',
    type: CampaignType.Automated,
  },
  loyaltyCompleted: {
    label: 'Loyalty Scheme Completed',
    messageContent: 'Hi {{ClientFirstName}}, thank you for your recent booking. As a thank you for your loyalty, here is a discount code for your next booking! Use the code {{LoyaltyPromocode}} the next time you book at {{BusinessWebsite}}. See you soon!',
    type: CampaignType.Automated,
  },
  loyaltyHalf: {
    label: 'Loyalty Scheme Halfway',
    messageContent: 'Hi {{ClientFirstName}}, thank you for your recent booking. You are halfway to unlocking your loyalty reward. Thank you for your loyalty!',
    type: CampaignType.Automated,
  },
  loyaltyAlmost: {
    label: 'Loyalty Scheme Almost Completed',
    messageContent: 'Hi {{ClientFirstName}}, thank you for your recent booking, you are close to unlocking your loyalty reward! Thank you for your loyalty!',
    type: CampaignType.Automated,
  },
  clientReferralCompleted: {
    label: 'Client Referral Completed',
    messageContent: 'Hi {{ClientFirstName}}, thank you for your recent referral. As a thank you for your support, here is a discount code for your next booking! Use the code {{LoyaltyPromocode}} the next time you book at {{BusinessWebsite}}. See you soon!',
    type: CampaignType.Automated,
  },
  clientReferralHalf: {
    label: 'Client Referral Scheme Halfway',
    messageContent: 'Hi {{ClientFirstName}}, thank you for your recent referral. You are halfway to unlocking your referral reward. Thank you for your support!',
    type: CampaignType.Automated,
  },
  clientReferralAlmost: {
    label: 'Client Referral Scheme Almost Completed',
    messageContent: 'Hi {{ClientFirstName}}, thank you for your recent referral, you are close to unlocking your referral reward! Thank you for your support!',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingAccepted]: {
    label: 'Booking accepted',
    messageContent: 'Your booking with {{BusinessName}} on {{BookingDateTime}} has been confirmed. Manage here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrRescheduleRejected]: {
    label: 'Booking accepted',
    messageContent: 'Your reschedule request with {{BusinessName}} on {{BookingDateTime}} has been rejected. Manage here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingCancellationRequested]: {
    label: 'Booking cancellation requested',
    messageContent: 'You have requested to cancel your booking with {{BusinessName}} on {{BookingDateTime}}.',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingCancelled]: {
    label: 'Booking cancelled',
    messageContent: 'Your booking with {{BusinessName}} on {{BookingDateTime}} has been cancelled. Book again here: {{BusinessWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingNoShow]: {
    label: 'Booking no-show',
    messageContent: 'Your booking with {{BusinessName}} on {{BookingDateTime}} has been marked as a no-show. Book again here: {{BusinessWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingCompleted]: {
    label: 'Booking completed',
    messageContent: 'Thank you for booking with {{BusinessName}}. Please leave a review of your experience here: {{ReviewLink}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingConsultationFormReminder]: {
    label: 'Consultation form reminder',
    messageContent: 'Your booking with {{BusinessName}} requires a consultation form to be completed here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingDepositReminder]: {
    label: 'Booking deposit reminder',
    messageContent: 'Your booking with {{BusinessName}} requires a deposit to be paid here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingDeclined]: {
    label: 'Booking declined',
    messageContent: 'Your booking with {{BusinessName}} on {{BookingDateTime}} has been declined. Book again here: {{BusinessWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingReminder]: {
    label: 'Booking reminder',
    messageContent: 'This is a reminder for your booking with {{BusinessName}} on {{BookingDateTime}}. Manage here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrReviewReminder]: {
    label: 'Review reminder',
    messageContent: 'Hi {{ClientFirstName}}, we hope you enjoyed your visit! If you have a moment, we\'d really appreciate you leaving a quick review. {{ReviewLink}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingRequested]: {
    label: 'Booking requested',
    messageContent: 'Your booking with {{BusinessName}} on {{BookingDateTime}} has been requested. Manage here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrBookingUpdated]: {
    label: 'Booking updated',
    messageContent: 'Your booking with {{BusinessName}} on {{BookingDateTime}} has been updated. Manage here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.TrPaymentReceipt]: {
    label: 'Payment receipt',
    messageContent: 'Payment received of {{PaymentAmount}} for your booking with {{BusinessName}} on {{BookingDateTime}}. Manage here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.GcRedemptionCode]: {
    label: 'Gift card code',
    messageContent: 'Your gift card {{GiftCardCode}} to use with {{BusinessName}}. {{GiftCardMessage}} Book here: {{BookingWebsite}}',
    type: CampaignType.Automated,
  },
  [AutomatedTriggerType.GcPaymentReceipt]: {
    label: 'Payment receipt',
    messageContent: 'Payment received of {{PaymentAmount}} for your gift card with {{BusinessName}}.',
    type: CampaignType.Automated,
  },
}

export function getEstimatedSmsUsage(messageLength: number) {
  const smsLimit = 160
  const threshold = 5
  const usage = Math.ceil(messageLength / smsLimit)
  const remainder = messageLength % smsLimit

  if (messageLength === 0) {
    return '0'
  }

  // If slightly over & over 1 not to show 0 to 1
  if (usage > 1 && remainder < threshold) {
    return `${usage - 1} to ${usage}`
  }

  // If slightly under
  if (remainder > (smsLimit - threshold)) {
    return `${usage} to ${usage + 1}`
  }

  return `${usage}`
}

export function getNext15MinuteMark(): Date {
  const now = new Date()
  const grace = 3 // Allow a grace period to cancel
  now.setMinutes(now.getMinutes() + grace)
  const minutes = now.getMinutes()
  const minutesToAdd = 15 - (minutes % 15)
  now.setMinutes(minutes + minutesToAdd)
  return now
}

interface ActionTriggeredConfig {
  description: string
  actionTriggered: true
}

export interface DelayedConfig {
  description: string
  actionTriggered: false
  manageDelay: DelayConfig
}

interface DelayConfig {
  minDaysOffset: number
  maxDaysOffset: number
  inverseDaysOffsetLabel: number
}

export function automationControlsConfig(triggerType: AutomatedTriggerType): ActionTriggeredConfig | DelayedConfig {
  switch (triggerType) {
    case AutomatedTriggerType.UpcomingBirthday:
      return {
        description: 'Sent to clients **$0** days before their upcoming birthday at **$1:00**.',
        actionTriggered: false,
        manageDelay: {
          minDaysOffset: 1,
          maxDaysOffset: 30,
          inverseDaysOffsetLabel: -1,
        },
      }
    case AutomatedTriggerType.Lapsed:
      return {
        description: 'Sent to clients who haven\'t rebooked within the specified time frame **$0** days after their last booking at **$1:00**.',
        actionTriggered: false,
        manageDelay: {
          minDaysOffset: 1,
          maxDaysOffset: 120,
          inverseDaysOffsetLabel: 1,
        },
      }
    case AutomatedTriggerType.OfflineBooking:
      return {
        description: 'Sent to clients who have have never booked online **$0** days after their last booking at **$1:00**.',
        actionTriggered: false,
        manageDelay: {
          minDaysOffset: 1,
          maxDaysOffset: 60,
          inverseDaysOffsetLabel: 1,
        },
      }
    case AutomatedTriggerType.FirstBooking:
      return {
        description: 'Sent to the clients **$0** days after their first booking at **$1:00**.',
        actionTriggered: false,
        manageDelay: {
          minDaysOffset: 1,
          maxDaysOffset: 60,
          inverseDaysOffsetLabel: 1,
        },
      }
    case AutomatedTriggerType.LoyaltyCompleted:
      return {
        description: 'Sent to clients after completing the loyalty scheme.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.LoyaltyAlmost:
      return {
        description: 'Sent to clients after reaching 90% completion of the loyalty scheme.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.LoyaltyHalf:
      return {
        description: 'Sent to clients after reaching 50% completion of the loyalty scheme.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.ClientReferralCompleted:
      return {
        description: 'Sent to clients after the referred client completed their booking.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.ClientReferralAlmost:
      return {
        description: 'Sent to clients after reaching 90% completion of the client referral target.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.ClientReferralHalf:
      return {
        description: 'Sent to clients after reaching 50% completion of the client referral target.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingAccepted:
      return {
        description: 'Sent to clients when you add a booking to your calendar or when you accept a booking request from your website.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingCancellationRequested:
      return {
        description: 'Sent to clients when they request to cancel a booking.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingCancelled:
      return {
        description: 'Sent to clients when you cancel a booking.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingNoShow:
      return {
        description: 'Sent to clients when you mark a booking as a no-show.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingCompleted:
      return {
        description: 'Sent to clients when you complete a booking.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingConsultationFormReminder:
      return {
        description: 'Sent to clients when you request a reminder to be sent to complete the consultation form.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingDepositReminder:
      return {
        description: 'Sent to clients when you request a reminder to be sent to paid the deposit.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingDeclined:
      return {
        description: 'Sent to clients when you decline a booking request from your website.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrRescheduleRejected:
      return {
        description: 'Sent to clients when you reject a reschedule request',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrReviewReminder:
      return {
        description: 'Sent **$0** calendar days after a booking is completed if the client has not left a review yet, at **$1:00**.',
        actionTriggered: false,
        manageDelay: {
          minDaysOffset: 1,
          maxDaysOffset: 60,
          inverseDaysOffsetLabel: 1,
        },
      }
    case AutomatedTriggerType.TrBookingReminder:
      return {
        description: 'Sent to the clients **$0** days before their booking at **$1:00**',
        actionTriggered: false,
        manageDelay: {
          minDaysOffset: 1,
          maxDaysOffset: 4,
          inverseDaysOffsetLabel: -1,
        },
      }
    case AutomatedTriggerType.TrBookingRequested:
      return {
        description: 'Sent to clients to confirm a booking request was made.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrBookingUpdated:
      return {
        description: "Sent to clients when you update a booking's details such as the date and time of the booking.",
        actionTriggered: true,
      }
    case AutomatedTriggerType.TrPaymentReceipt:
      return {
        description: 'Sent to clients when they made a payment for a booking.',
        actionTriggered: true,
      }
    case AutomatedTriggerType.GcPaymentReceipt:
      return {
        description: 'Sent to clients when they make a payment for a gift card.',
        actionTriggered: true,
      }

    case AutomatedTriggerType.GcRedemptionCode:
      return {
        description: 'Sent to clients when they receive a gift card.',
        actionTriggered: true,
      }
    default:
      return {
        description: '',
        actionTriggered: false,
        manageDelay: {
          minDaysOffset: 1,
          maxDaysOffset: 60,
          inverseDaysOffsetLabel: 1,
        },
      }
  }
}

export const labels = {
  [AutomatedTriggerType.FirstBooking]: 'First Booking',
  [AutomatedTriggerType.Lapsed]: 'Lapsed',
  [AutomatedTriggerType.OfflineBooking]: 'Promote Website',
  [AutomatedTriggerType.UpcomingBirthday]: 'Upcoming Birthday',
  [AutomatedTriggerType.TrBookingAccepted]: 'Booking Accepted',
  [AutomatedTriggerType.TrBookingCancellationRequested]: 'Booking Cancellation Requested',
  [AutomatedTriggerType.TrBookingCancelled]: 'Booking Cancelled',
  [AutomatedTriggerType.TrBookingNoShow]: 'Booking No-Show',
  [AutomatedTriggerType.TrBookingCompleted]: 'Booking Completed',
  [AutomatedTriggerType.TrBookingConsultationFormReminder]: 'Consultation Reminder',
  [AutomatedTriggerType.TrBookingDepositReminder]: 'Deposit Reminder',
  [AutomatedTriggerType.TrBookingDeclined]: 'Booking Declined',
  [AutomatedTriggerType.TrRescheduleRejected]: 'Reschedule Rejected',
  [AutomatedTriggerType.TrBookingReminder]: 'Booking Reminder',
  [AutomatedTriggerType.TrReviewReminder]: 'Review Reminder',
  [AutomatedTriggerType.TrBookingRequested]: 'Booking Requested',
  [AutomatedTriggerType.TrBookingUpdated]: 'Booking Updated',
  [AutomatedTriggerType.TrPaymentReceipt]: 'Payment Receipt',
  [AutomatedTriggerType.LoyaltyAlmost]: 'Almost Completed',
  [AutomatedTriggerType.LoyaltyCompleted]: 'Completed',
  [AutomatedTriggerType.LoyaltyHalf]: 'Halfway Completed',
  [AutomatedTriggerType.ClientReferralAlmost]: 'Almost Completed',
  [AutomatedTriggerType.ClientReferralCompleted]: 'Completed',
  [AutomatedTriggerType.ClientReferralHalf]: 'Halfway Completed',
  [AutomatedTriggerType.GcRedemptionCode]: 'Gift Card Details',
  [AutomatedTriggerType.GcPaymentReceipt]: 'Gift Card Payment Receipt',
}

export type ItemType = {
  _id: string
  type: AutomatedTriggerType
  label: string
  email: EmailCampaignModel | undefined
  sms: SmsCampaignModel | undefined
  emailColor: 'green' | 'gray'
  emailIcon: 'check' | 'x'
  smsColor: 'green' | 'gray'
  smsIcon: 'check' | 'x'
  showDangerLabelColor: boolean
  description: string
  hideSmsSettings: boolean
}

export const createUrlId = (index: number, value: string) => {
  return (index + value).split('')
    .reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0)
    .toString(36)
}

export const createDataItem = (
  timezone: string,
  triggerGroupType: string,
  type: AutomatedTriggerType,
  index: number,
  emailCampaign?: EmailCampaignModel,
  smsCampaign?: SmsCampaignModel,
): ItemType => {

  const label = labels[type] ?? 'Unknown'
  const emailIsActive = emailCampaign?.data.status === CampaignStatus.Active
  const smsIsActive = smsCampaign?.data.status === CampaignStatus.Active
  const isGiftCardMessage = type.includes('gc_')
  const hideSmsSettings = isGiftCardMessage
  const showDangerLabelColor = !emailIsActive && !smsIsActive &&
    (triggerGroupType === 'transactional' || type === AutomatedTriggerType.LoyaltyCompleted || type === AutomatedTriggerType.ClientReferralCompleted)

  let description = ''

  if (triggerGroupType === 'default') {
    let daysOffset = 1
    let sendAtHour = 12
    let inverseDaysOffsetLabel = 1

    const config = automationControlsConfig(type)
    if ('manageDelay' in config) {
      inverseDaysOffsetLabel = config.manageDelay.inverseDaysOffsetLabel
    }

    if (emailCampaign) {
      daysOffset = emailCampaign.automatedCampaignData.daysOffset ?? 1
      sendAtHour = emailCampaign.automatedCampaignData.sendAtHour ?? 12
    } else if (smsCampaign) {
      daysOffset = smsCampaign.automatedCampaignData.daysOffset ?? 1
      sendAtHour = smsCampaign.automatedCampaignData.sendAtHour ?? 12
    } else {
      daysOffset = defaultAutomationSettings[type].daysOffset ?? 1
      sendAtHour = 12
    }

    description = `Sent **${daysOffset * inverseDaysOffsetLabel}** days ${inverseDaysOffsetLabel === 1 ? 'after' : 'before'} at **${sendAtHourInLocalTime(sendAtHour, timezone)}:00**.`
  }

  return {
    _id: createUrlId(index, type),
    type,
    label,
    email: emailCampaign,
    sms: smsCampaign,
    showDangerLabelColor,
    emailColor: emailIsActive ? 'green' : 'gray',
    emailIcon: emailIsActive ? 'check' : 'x',
    smsColor: smsIsActive ? 'green' : 'gray',
    smsIcon: smsIsActive ? 'check' : 'x',
    description,
    hideSmsSettings,
  }
}

export const validateEmailData = (data: EmailCampaign, errors?: Record<string, string>) => {
  const validation = {
    emailSubject: {
      length: {
        minimum: 1,
        message: '^ Subject cannot be empty',
      },
    },
  }

  errors = {}

  const validateErrors = validate(data, validation) || {}

  for (const property in validateErrors) {
    errors[property] = formatErrorMessage(errors[property])
  }

  return Object.keys(errors).length === 0
}

export const validateSmsData = (data: SmsCampaign, previewText: string) => {
  const validation = {
    messageContent: {
      length: {
        minimum: 1,
        message: 'Text cannot be empty',
      },
    },
  }

  const errors: Record<string, string> = {}

  const validateErrors = validate(data, validation) || {}

  const regex = emojiRegex()
  if (previewText.match(regex)) errors.messageContent = 'Your message cannot include emojis.'
  if (previewText.includes('[PROMOCODE]')) errors.messageContent = 'Please replace [PROMOCODE] in your message with your promo code.'
  if (previewText.includes('{') || previewText.includes('}')) errors.messageContent = 'Your message contains illegal { or } characters'

  for (const property in validateErrors) {
    errors[property] = formatErrorMessage(errors[property])
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const isGeneratedBlock = (blockType: EmailContentBlockType) => {
  return ([
    EmailContentBlockType.CancellationPolicy,
    EmailContentBlockType.DeclineReason,
    EmailContentBlockType.BookingItems,
    EmailContentBlockType.Ctas,
    EmailContentBlockType.BookingDateAndLocation,
    EmailContentBlockType.LoyaltyScheme,
    EmailContentBlockType.ClientReferralScheme,
    EmailContentBlockType.SocialLinks,
    EmailContentBlockType.GiftCardDetails,
    EmailContentBlockType.GiftCardReceipt,
  ] as string[]).includes(blockType)
}

export const sendAtHourInLocalTime = (sendAtHour: number, timezone: string) => {
  const utcTime = moment.utc().startOf('day').hour(sendAtHour)
  return utcTime.tz(timezone).format('HH')
}